import LogoutIcon from "@heroicons/react/outline/LogoutIcon";
import * as React from "react";
import { useTranslation } from "react-i18next";
import Undraw from "react-undraw";
import { colors } from "~/config/stitches/colors";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { Column, Columns } from "~/design-system/Columns";
import { Heading } from "~/design-system/Heading";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { useLogout } from "~/screens/App/utils/useLogout/useLogout";

type UnavailableStateViewProps = {
  email?: string;
  variant: "locked" | "notProvisioned";
};

function UnavailableStateView({
  email,
  variant,
}: UnavailableStateViewProps): JSX.Element {
  const { t } = useTranslation();
  const { logout } = useLogout();
  const isNotProvisioned = variant === "notProvisioned";
  const contactSubject = isNotProvisioned
    ? "Contact-Account-Unavailable"
    : "Contact-Locked";
  const explanation = isNotProvisioned
    ? t(
        "screens.Unavailable.notProvisionedExplanation",
        "Your account was authenticated, but it is not available in Clovis yet. Contact our team or sign out and use a different account."
      )
    : t(
        "screens.Unavailable.explanation",
        "To know more about it, contact our teams by using the button below or send us an email."
      );

  return (
    <Box
      display="flex"
      height="screen"
      width="screen"
      justifyContent="center"
      alignItems="center"
      padding="gutter"
    >
      <Columns space="large" width="auto" collapseBelow="tablet">
        <Column width="3-5" alignY="center">
          <Stack space="large">
            <Heading level="1">
              {t("screens.Unavailable.title", "Account unavailable")}
            </Heading>
            {email ? <Heading level="3">{email}</Heading> : null}
            <Text>{explanation} support@clovis.pro</Text>
            <Columns space="gutter">
              <Column width="content" alignY="center">
                <Button
                  size="large"
                  variant="solid"
                  tone="brandAccent"
                  dataClovisActionId="page-unavailable-button"
                  as="a"
                  href={`mailto:support@clovis.pro?subject=${contactSubject}`}
                >
                  {t("screens.Unavailable.contactUs", "Contact us")}
                </Button>
              </Column>
              <Column align="left" alignY="center">
                <Button
                  size="large"
                  variant="ghost"
                  trailingIcon={<LogoutIcon />}
                  onClick={() => void logout()}
                  dataClovisActionId="unavailable-user-log-out"
                >
                  {t("screens.Unavailable.Header.logout", "Log out")}
                </Button>
              </Column>
            </Columns>
          </Stack>
        </Column>
        <Column alignY="center">
          <Undraw
            name="unavailable"
            primaryColor={colors.emerald500}
            height="100%"
          />
        </Column>
      </Columns>
    </Box>
  );
}

export { UnavailableStateView };
